import { createSlice } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'

const initialState = {
    selectedItemsIds: [],
    expandedItemsIds: [],
    collapsedItemsIds: [],
    filteredItemsIds: [],
    updateQueue: [],
    adjustmentQueue: [],
    filterOption: 'showAllItems',
    searchQuery: '',
    isCalculatingQuote: false,
    isUpdatingDrawingUnits: false,
    selectedQuoteItemDrawingId: null,
    openDrawingDoctorModal: false,
    openPartQuoteHistoryModal: false,
    quoteStatus: null,
    quotePaymentStatus: null,
    selectedPartFromLibrary: null,
}

export const quoteItemsSlice = createSlice({
    name: 'quoteItemsSlice',
    initialState,
    reducers: {
        setSelectedItemsIds: (state, action) => {
            state.selectedItemsIds = action.payload
        },
        addSelectedItem: (state, action) => {
            state.selectedItemsIds.push(action.payload)
        },
        removeSelectedItem: (state, action) => {
            state.selectedItemsIds = state.selectedItemsIds.filter((item) => item !== action.payload)
        },
        clearSelectedItems: (state) => {
            state.selectedItemsIds = []
        },
        setExpandedItemsIds: (state, action) => {
            state.expandedItemsIds = action.payload
        },
        addExpandedItem: (state, action) => {
            state.expandedItemsIds.push(action.payload)
        },
        removeExpandedItem: (state, action) => {
            state.expandedItemsIds = state.expandedItemsIds.filter((item) => item !== action.payload)
        },
        clearExpandedItems: (state) => {
            state.expandedItemsIds = []
        },
        setCollapsedItemsIds: (state, action) => {
            state.collapsedItemsIds = action.payload
        },
        addCollapsedItem: (state, action) => {
            state.collapsedItemsIds.push(action.payload)
        },
        removeCollapsedItem: (state, action) => {
            state.collapsedItemsIds = state.collapsedItemsIds.filter((item) => item !== action.payload)
        },
        clearCollapsedItems: (state) => {
            state.collapsedItemsIds = []
        },
        setFilterOption: (state, action) => {
            state.filterOption = action.payload
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload
        },
        setIsCalculatingQuote: (state, action) => {
            state.isCalculatingQuote = action.payload
        },
        setIsUpdatingDrawingUnits: (state, action) => {
            state.isUpdatingDrawingUnits = action.payload
        },

        setSelectedQuoteItemDrawingId: (state, action) => {
            state.selectedQuoteItemDrawingId = action.payload
        },
        setOpenDrawingDoctorModal: (state, action) => {
            state.openDrawingDoctorModal = action.payload
        },
        setOpenPartQuoteHistoryModal: (state, action) => {
            state.openPartQuoteHistoryModal = action.payload
        },
        setSelectedQuoteStatus: (state, action) => {
            state.quoteStatus = action.payload
        },
        setSelectedQuotePaymentStatus: (state, action) => {
            state.quotePaymentStatus = action.payload
        },
        setSelectedPartFromLibrary: (state, action) => {
            state.selectedPartFromLibrary = action.payload
        },
        setFilteredItemsIds: (state, action) => {
            state.filteredItemsIds = action.payload
        },
        addItemToUpdateQueue: (state, action) => {
            if (!state.updateQueue.includes(action.payload)) {
                state.updateQueue.push(action.payload)
            }
        },
        removeItemFromUpdateQueue: (state, action) => {
            state.updateQueue = state.updateQueue.filter((item) => item !== action.payload)
        },
        clearUpdateQueue: (state) => {
            state.updateQueue = []
        },
        addItemToAdjustmentQueue: (state, action) => {
            const existingItemIndex = state.adjustmentQueue.findIndex((item) => item.id === action.payload.id)
            if (existingItemIndex !== -1) {
                const newQueue = [...state.adjustmentQueue]

                newQueue[existingItemIndex] = {
                    ...action.payload,
                    otherChargePrice: action.payload.otherChargePrice,
                }
                state.adjustmentQueue = newQueue
            } else {
                state.adjustmentQueue = [
                    ...state.adjustmentQueue,
                    {
                        ...action.payload,
                        otherChargePrice: action.payload.otherChargePrice,
                    },
                ]
            }
        },
        clearAdjustmentQueue: (state) => {
            state.adjustmentQueue = []
        },
    },
})

export const selectSelectedItems = (state) => state.quoteItemsSlice.selectedItemsIds
export const selectExpandedItems = (state) => state.quoteItemsSlice.expandedItemsIds
export const selectCollapsedItems = (state) => state.quoteItemsSlice.collapsedItemsIds
export const selectFilterOption = (state) => state.quoteItemsSlice.filterOption
export const selectSearchQuery = (state) => state.quoteItemsSlice.searchQuery
export const selectIsCalculatingQuote = (state) => state.quoteItemsSlice.isCalculatingQuote
export const selectIsUpdatingDrawingUnits = (state) => state.quoteItemsSlice.isUpdatingDrawingUnits
export const selectQuoteItemDrawingId = (state) => state.quoteItemsSlice.selectedQuoteItemDrawingId
export const selectOpenDrawingDoctorModal = (state) => state.quoteItemsSlice.openDrawingDoctorModal
export const selectOpenPartQuoteHistoryModal = (state) => state.quoteItemsSlice.openPartQuoteHistoryModal
export const selectedQuoteStatus = (state) => state.quoteItemsSlice.quoteStatus
export const selectQuotePaymentStatus = (state) => state.quoteItemsSlice.quotePaymentStatus
export const selectSelectedPartFromLibrary = (state) => state.quoteItemsSlice.selectedPartFromLibrary
export const selectFilteredItemsIds = (state) => state.quoteItemsSlice.filteredItemsIds
export const selectIsUpdateQueueEmpty = (state) => state.quoteItemsSlice.updateQueue.length === 0
export const selectIsAdjustmentQueueEmpty = (state) => state.quoteItemsSlice.adjustmentQueue.length === 0
export const selectAdjustmentQueueItems = (state) => state.quoteItemsSlice.adjustmentQueue

export const {
    addCollapsedItem,
    addExpandedItem,
    addItemToAdjustmentQueue,
    addItemToUpdateQueue,
    addSelectedItem,
    clearAdjustmentQueue,
    clearCollapsedItems,
    clearExpandedItems,
    clearSelectedItems,
    clearUpdateQueue,
    removeCollapsedItem,
    removeExpandedItem,
    removeItemFromUpdateQueue,
    removeSelectedItem,
    setCollapsedItemsIds,
    setExpandedItemsIds,
    setFilterOption,
    setFilteredItemsIds,
    setIsCalculatingQuote,
    setIsUpdatingDrawingUnits,
    setOpenDrawingDoctorModal,
    setOpenPartQuoteHistoryModal,
    setSearchQuery,
    setSelectedItemsIds,
    setSelectedPartFromLibrary,
    setSelectedQuoteItemDrawingId,
    setSelectedQuotePaymentStatus,
    setSelectedQuoteStatus,
} = quoteItemsSlice.actions

export default quoteItemsSlice.reducer
